import { Chat, useChat, type UseChatHelpers } from "@ai-sdk/react";
import { useRequiredContext } from "@redotech/react-util/context";
import { useEscalationContext } from "@redotech/redo-merchant-app-common/escalation";
import {
  type AgentToolResultHandlers,
  GlobalAgentContext,
} from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { PageContext } from "@redotech/redo-model/agents/page-context";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { LRUCache } from "@redotech/util/cache";
import { useQueryClient } from "@tanstack/react-query";
import { lastAssistantMessageIsCompleteWithApprovalResponses } from "ai";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getCustomAnalyticsReportFilterOptionsQueryKey } from "../../../analytics/reports/custom/hooks/use-custom-analytics-report-filter-options-query";
import { getCustomAnalyticsReportQueryInvalidationKey } from "../../../analytics/reports/custom/hooks/use-custom-analytics-report-query";
import { getCustomAnalyticsReportRevisionsQueryInvalidationKey } from "../../../analytics/reports/custom/hooks/use-custom-analytics-report-revisions-query";
import { getCustomAnalyticsReportRowsQueryInvalidationKey } from "../../../analytics/reports/custom/hooks/use-custom-analytics-report-rows-query";
import { getCustomAnalyticsReportsQueryInvalidationKey } from "../../../analytics/reports/custom/hooks/use-custom-analytics-reports-query";
import { tryParseCustomAnalyticsReportAction } from "../custom-analytics-report-tool-actions";
import {
  AskRedoRpcTransport,
  type EscalationSnapshot,
} from "../transport/ask-redo-rpc-transport";
import type { AskRedoUIMessage } from "../types";

/**
 * Module-level cache of `Chat` instances, keyed by either a local
 * "new-<uuid>" id (for conversations that haven't been assigned a
 * server threadId yet) OR the server-assigned `LlmThread._id`.
 *
 * Why module-level: AI SDK's designed pattern for "move a thread
 * from one component to another" is to pass the SAME Chat instance
 * (via `useChat({ chat })`) to both components. A module cache lets
 * the slide-out and the full-page look up the same instance so the
 * user's conversation survives the transition (no re-hydration, no
 * lost in-flight stream, no `setMessages` dance).
 *
 * Sequence:
 *   1. Slide-out mounts. `chatId = "new-abc"`. Cache miss → factory
 *      creates Chat, stored at "new-abc".
 *   2. Server assigns threadId "T". The transport's
 *      `onThreadIdCaptured` callback re-keys the cached Chat under
 *      "T" AND removes the "new-abc" entry so it doesn't linger.
 *   3. User clicks Expand → navigates to /ask-redo/T.
 *   4. Full-page looks up "T" → cache hit → same Chat. Conversation
 *      survives the transition intact.
 *
 * LRU-capped via the shared `LRUCache` primitive — the cache
 * survives until the tab unloads, so unbounded growth is real.
 */
const CHAT_CACHE_CAPACITY = 20;
/**
 * Exported so the layout-level escalation watcher can push live human
 * messages into the right Chat instance (keyed by triageThreadId)
 * even when the slide-out UI is unmounted. The watcher is the
 * single SSE consumer; this cache is the seam between the data
 * layer and any mounted UI.
 */
export const chatInstances = new LRUCache<string, Chat<AskRedoUIMessage>>(
  CHAT_CACHE_CAPACITY,
);

/**
 * OUTBOUND tool-result seam. A tool like `update-portal-customization` streams
 * a patch as its output; the portal builder page registers a handler (keyed by
 * tool name in GlobalAgentContext) that applies that patch to its local draft.
 * The AI-SDK pipeline has no equivalent of the legacy chat's `onToolResult`
 * push, so we bridge it through the Chat instance's native `onFinish`. Generic
 * by design — any tool whose name has a registered handler fires.
 *
 * Why `onFinish` rather than an effect that scans `chat.messages`:
 *  - It fires exactly once per finished assistant turn, and only for freshly
 *    STREAMED messages — never for messages injected via `setMessages` during
 *    thread hydration. So reopening a thread that contains a historical portal
 *    patch does NOT re-apply it over the merchant's current draft.
 *  - It's bound to the Chat instance at construction, so the slide-out and
 *    full-page mounts that share one cached Chat can't double-apply — no
 *    per-instance dedupe bookkeeping needed.
 */
export function applyFinishedToolResults(
  message: AskRedoUIMessage,
  handlers: AgentToolResultHandlers | undefined,
  options?: { onCustomAnalyticsReportChanged?: () => void },
): void {
  let customAnalyticsReportChanged = false;

  for (const part of message.parts) {
    if (part.type !== "dynamic-tool" || part.state !== "output-available") {
      continue;
    }
    handlers?.[part.toolName]?.(part.output);
    customAnalyticsReportChanged ||= isCustomAnalyticsReportChangeOutput({
      toolName: part.toolName,
      output: part.output,
    });
  }

  if (customAnalyticsReportChanged) {
    options?.onCustomAnalyticsReportChanged?.();
  }
}

function isCustomAnalyticsReportChangeOutput({
  toolName,
  output,
}: {
  toolName: string;
  output: unknown;
}): boolean {
  return (
    tryParseCustomAnalyticsReportAction(toolName, output)?.type ===
    "view-report"
  );
}

/**
 * Module-level "latest" page context + timezone. Written inside a
 * post-render effect by the active mount, read by every cached
 * transport at send time.
 *
 * Why module-level (not per-hook ref): `Chat` instances are cached
 * across mounts. Their transport closes over whatever ref existed
 * at the mount that CREATED the Chat. A later mount has a brand-new
 * ref the cached transport never sees, so per-mount refs go stale
 * across the slide-out → full-page handoff. Module-level publish
 * solves this: any mount's write is visible to any cached transport.
 *
 * Why in an effect (not during render): writing during render
 * violates React's render-purity contract — a discarded render
 * (Suspense replay, transition) would still publish, possibly
 * clobbering a live mount's values. Effects only run after a
 * committed render, which is exactly the invariant we need.
 */
let latestPageContext: PageContext | undefined;
let latestMerchantTimezone: string | undefined;
let latestEscalationSnapshot: EscalationSnapshot | undefined;
/**
 * Latest page-registered tool-result handlers, published module-level so the
 * cached Chat's `onFinish` (set once at construction) always reads the current
 * map. Same rationale as `latestPageContext`: the Chat instance outlives the
 * mount that created it, so a per-mount closure would go stale.
 */
let latestToolResultHandlers: AgentToolResultHandlers | undefined;
let latestInvalidateCustomAnalyticsReports: (() => void) | undefined;

/**
 * Wraps `useChat` with the Ask Redo RPC transport + auto-resume-
 * after-approval semantics, sharing `Chat` instances across mounts
 * via the module cache above so state survives slide-out→full-page
 * navigation.
 *
 * `initialThreadId` is the server-assigned `LlmThread._id` if the
 * caller is hydrating an existing thread (deep-link / expand). When
 * present it's both the cache key and the initial threadId state.
 * The hook generates its own stable local id for fresh
 * conversations — callers shouldn't be juggling `chatId`s themselves.
 *
 * Return shape:
 *   - `chat` — `UseChatHelpers` (sendMessage, messages, status, etc.)
 *   - `threadId` — current server thread id. Starts as
 *     `initialThreadId` and flips to the real value after the first
 *     `complete` event.
 *   - `isStreaming` — convenience derivation of
 *     `chat.status === "streaming" || "submitted"`. Returned here so
 *     callers (page, slide-out, conversation) don't re-derive it
 *     three different times.
 */
export function useAskRedoChat(
  initialThreadId?: string,
  initialMessages?: AskRedoUIMessage[],
): {
  chat: UseChatHelpers<AskRedoUIMessage>;
  threadId: string | undefined;
  isStreaming: boolean;
  requestEmergencyEscalation: () => Promise<void>;
  emergencyEscalationPending: boolean;
  emergencyEscalationError: string | null;
} {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const queryClient = useQueryClient();
  const { pageContext, toolResultHandlers } = useContext(GlobalAgentContext);
  const { escalation, setEscalation, setTriageThreadId } =
    useEscalationContext();
  const invalidateCustomAnalyticsReports = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportsQueryInvalidationKey(team._id),
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportFilterOptionsQueryKey(team._id),
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportQueryInvalidationKey(team._id),
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportRowsQueryInvalidationKey(team._id),
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportRevisionsQueryInvalidationKey(team._id),
      exact: false,
    });
  }, [queryClient, team._id]);

  // Stable per-mount local id used when there's no server threadId
  // yet. Initialised from `initialThreadId` when present so a
  // deep-link straight to /ask-redo/T hits the cache by T from the
  // very first render.
  const [chatId] = useState(
    () => initialThreadId ?? `new-${crypto.randomUUID()}`,
  );
  const [threadId, setThreadId] = useState<string | undefined>(initialThreadId);
  const [emergencyEscalationPending, setEmergencyEscalationPending] =
    useState(false);
  const [emergencyEscalationError, setEmergencyEscalationError] = useState<
    string | null
  >(null);
  const emergencyEscalationInFlightRef = useRef(false);

  // Mirror the chat's server-assigned threadId into EscalationContext
  // so the escalate-to-human card has a triageThreadId to attach the
  // Intercom conversation to. The slide-out's "fresh conversation"
  // effect intentionally clears it on open; we re-populate as soon as
  // the agent assigns a real LlmThread._id.
  useEffect(() => {
    if (threadId) {
      setTriageThreadId(threadId);
    }
  }, [threadId, setTriageThreadId]);

  // Publish the latest page context + timezone + escalation snapshot
  // for any cached transport to read on its next `sendMessages` call.
  // Effect so it only runs after the render commits.
  useEffect(() => {
    latestPageContext = pageContext;
    latestMerchantTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, [pageContext]);

  // Publish latest callbacks for the cached Chat's `onFinish` to read.
  // Effect so it only runs after the render commits.
  useEffect(() => {
    latestToolResultHandlers = toolResultHandlers;
    latestInvalidateCustomAnalyticsReports = invalidateCustomAnalyticsReports;
  }, [toolResultHandlers, invalidateCustomAnalyticsReports]);

  useEffect(() => {
    latestEscalationSnapshot = {
      status: escalation.status,
      escalationId: escalation.escalationId,
      // Publish the bound triageThreadId (NOT the floating
      // `EscalationContext.triageThreadId` setter, which gets
      // overwritten every time the merchant clicks a different
      // history thread). The transport scopes diversion to the
      // chat bound to this exact thread — without it, a fresh
      // chat on a different thread would still route into the
      // active Intercom conversation.
      triageThreadId: escalation.triageThreadId,
    };
  }, [escalation.status, escalation.escalationId, escalation.triageThreadId]);

  // Stable setter ref so the transport's useMemo doesn't invalidate
  // each render (which would reset its `threadIdByChatId` map).
  const setThreadIdRef = useRef(setThreadId);
  setThreadIdRef.current = setThreadId;
  const setEscalationRef = useRef(setEscalation);
  setEscalationRef.current = setEscalation;

  // Preferred cache key: real threadId if we have one, else the
  // local chatId. When the server assigns a threadId mid-session
  // the transport callback below re-keys the existing Chat under
  // the new key AND drops the old `new-<uuid>` entry.
  const cacheKey = threadId ?? chatId;

  const transport = useMemo(
    () =>
      new AskRedoRpcTransport(rpcClient, {
        onThreadIdCaptured: (capturedForChatId, tid) => {
          setThreadIdRef.current(tid);
          const existing = chatInstances.get(capturedForChatId);
          if (existing) {
            // Re-key the cached Chat under the server threadId.
            // Drop the `new-<uuid>` entry — keeping it would leave
            // a permanent zombie reference to the same Chat (and
            // double the cache footprint per thread).
            if (capturedForChatId !== tid) {
              chatInstances.delete(capturedForChatId);
            }
            chatInstances.set(tid, existing);
          }
        },
        getPageContext: () => latestPageContext,
        getMerchantTimezone: () => latestMerchantTimezone,
        getEscalation: () => latestEscalationSnapshot,
        onEmergencyEscalationCreated: (event) => {
          setEscalationRef.current({
            escalationId: event.escalation.escalationId,
            intercomConversationId: event.escalation.intercomConversationId,
            noticeTopic: event.escalation.noticeTopic,
            status: event.escalation.status,
            triageThreadId: event.threadId,
          });
        },
      }),
    [rpcClient],
  );

  const chatInstance = useMemo(() => {
    const existing = chatInstances.get(cacheKey);
    if (existing) {
      return existing;
    }
    const fresh = new Chat<AskRedoUIMessage>({
      id: cacheKey,
      messages: initialMessages,
      transport,
      sendAutomaticallyWhen:
        lastAssistantMessageIsCompleteWithApprovalResponses,
      onFinish: ({ message }) =>
        applyFinishedToolResults(message, latestToolResultHandlers, {
          onCustomAnalyticsReportChanged:
            latestInvalidateCustomAnalyticsReports,
        }),
    });
    chatInstances.set(cacheKey, fresh);
    return fresh;
    // `initialMessages` intentionally NOT in deps — it's captured
    // at construction and re-applied via the hydration effect
    // below if it arrives late. Including it would blow away the
    // cached Chat on every re-render that produced a new
    // initialMessages array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, transport]);

  const chat = useChat<AskRedoUIMessage>({ chat: chatInstance });

  const requestEmergencyEscalation = useCallback(async () => {
    if (emergencyEscalationInFlightRef.current) {
      return;
    }
    emergencyEscalationInFlightRef.current = true;
    setEmergencyEscalationPending(true);
    setEmergencyEscalationError(null);
    try {
      await transport.requestEmergencyEscalation({ chatId });
    } catch (error) {
      console.error("[ask-redo] emergency support handoff failed", error);
      setEmergencyEscalationError(
        error instanceof Error
          ? error.message
          : "Failed to connect to support.",
      );
    } finally {
      emergencyEscalationInFlightRef.current = false;
      setEmergencyEscalationPending(false);
    }
  }, [chatId, transport]);

  // Completed tool results are dispatched to page-registered handlers via the
  // Chat instance's `onFinish` (see `applyFinishedToolResults`), not here.

  // Late hydration: for the "direct URL load" path (no slide-out
  // predecessor → cache miss), the Chat was created with empty
  // messages because `useAskRedoThreadHistory` loads asynchronously.
  // Inject the messages once they arrive — but only once per Chat
  // instance, gated by a ref so a user typing during the load
  // can't be clobbered by a late-resolving fetch.
  const hydratedChatsRef = useRef<WeakSet<Chat<AskRedoUIMessage>>>(
    new WeakSet(),
  );
  useEffect(() => {
    if (!initialMessages || initialMessages.length === 0) {
      return;
    }
    if (hydratedChatsRef.current.has(chatInstance)) {
      return;
    }
    if (chat.messages.length > 0) {
      // The user beat us to it (typed + sent before history
      // resolved). Mark hydrated so we don't try again on the
      // next render.
      hydratedChatsRef.current.add(chatInstance);
      return;
    }
    hydratedChatsRef.current.add(chatInstance);
    chat.setMessages(initialMessages);
  }, [initialMessages, chatInstance, chat]);

  // Live human-reply injection lives in AskRedoEscalationWatcher
  // (layout level). It looks up THIS Chat instance from
  // `chatInstances` keyed by triageThreadId and assigns
  // `chat.messages = [...]` (the AbstractChat setter — `setMessages`
  // is only on the useChat hook return, not on the instance). The
  // ReactChatState setter fires subscribed useSyncExternalStore
  // callbacks so every mounted useChat re-renders with the appended
  // message — works whether the slide-out is mounted or not. The
  // agentName + agentAvatarUrl are exposed via
  // EscalationAuthorContext, so the banner and message bubble
  // renderers read them via a hook.

  const isStreaming =
    chat.status === "streaming" || chat.status === "submitted";

  return {
    chat,
    threadId,
    isStreaming,
    requestEmergencyEscalation,
    emergencyEscalationPending,
    emergencyEscalationError,
  };
}
